import type { PrototyperMessageFields } from '@ideoz/core';
import { planMessageHasPersistedBuild, planMessageHasUnfinishedBuild } from '@ideoz/core';
import type { WorkbenchSnapshotBootstrap } from '../usePrototyperWorkbenchFacade';
import {
  resumeTurnVersionDecision,
  settledTurnVersionDecision,
  type TurnVersionsLookup,
} from './turnVersions';

/**
 * - `unfinished`: a build session exists for the turn but never finished and left no snapshot to
 *   resume. The caller shows the retry state instead of starting a paid build on its own.
 * - `resume-fix`: the turn's version failed with an automatic fix attempt left; restore its
 *   files and start the fix stream (P6.4c).
 * - `resume-from-files`: an unfinished draft without a build-start snapshot; build the files
 *   the builder saved for its turn (P6.4d-3).
 */
export type BuildStreamStartDecision =
  | 'start'
  | 'skip'
  | 'wait'
  | 'resume-build'
  | 'resume-fix'
  | 'resume-from-files'
  | 'unfinished';

export type SnapshotIndexEntry = {
  llmInteractionId: string;
  snapshotId: string;
  revision: number;
  buildVerified?: boolean;
  createdAt?: string;
  prototyperDemoLinkId?: string;
};

/**
 * Whether to POST …/build-stream for a prototyper plan turn.
 */
export function shouldStartBuildStream(args: {
  planLlmInteractionId: string;
  builtTurnIds: ReadonlySet<string>;
  bootstrap: WorkbenchSnapshotBootstrap;
  planMessage?: PrototyperMessageFields | null;
  snapshotIndex?: ReadonlyMap<string, SnapshotIndexEntry>;
  getSnapshotIndexEntry?: (llmInteractionId: string) => SnapshotIndexEntry | undefined;
  snapshotIndexLoading?: boolean;
  /** One version per turn from the server (P6.4c). While `ready`, it decides over `builtTurnIds`. */
  turnVersions?: TurnVersionsLookup;
  /** This tab's lease owner id (`getWorkbenchTabId`). */
  ownerTabId?: string;
  now?: number;
}): BuildStreamStartDecision {
  const {
    planLlmInteractionId,
    builtTurnIds,
    bootstrap,
    planMessage,
    snapshotIndex,
    getSnapshotIndexEntry,
    snapshotIndexLoading,
    turnVersions,
    ownerTabId = '',
    now,
  } = args;

  const versionsReady = turnVersions?.status === 'ready';
  const version = versionsReady ? turnVersions.get(planLlmInteractionId) : undefined;

  // The browser's list only decides while the versions API does not answer.
  if (!versionsReady && builtTurnIds.has(planLlmInteractionId)) {
    return 'skip';
  }
  if (version && settledTurnVersionDecision(version, ownerTabId, now) === 'skip') {
    return 'skip';
  }

  const indexed =
    getSnapshotIndexEntry?.(planLlmInteractionId) ?? snapshotIndex?.get(planLlmInteractionId);
  if (indexed?.buildVerified === true) {
    return 'skip';
  }

  // Both waits come before the resume/skip branches below: while the index is still loading,
  // a missing entry is indistinguishable from "this turn was never snapshotted", and a resume
  // must never race the bootstrap restore for ownership of the container.
  if (snapshotIndexLoading || turnVersions?.status === 'loading') {
    return 'wait';
  }

  if (bootstrap === 'pending') {
    return 'wait';
  }

  // The turn's files were snapshotted at build-start but the build never got verified — the
  // build was interrupted (refresh/closed tab). Finish it locally instead of burning a whole
  // LLM turn re-generating code we already have.
  if (version) {
    const resumed = resumeTurnVersionDecision(version, indexed != null);
    if (resumed) return resumed;
  }

  if (indexed != null) {
    return 'resume-build';
  }

  // Deliberately last: a turn with a persisted build but no snapshot at all predates
  // build-start snapshots (or its upload failed). There is nothing to resume from, so keep
  // the historical behavior and leave it alone rather than restarting it.
  if (planMessage && planMessageHasPersistedBuild(planMessage)) {
    return 'skip';
  }

  // The build session was created (tab closed during planning or file writing) but nothing
  // finished and nothing was snapshotted. Starting a build here would spend credits without the
  // founder asking; skipping silently used to leave no prototype and no way forward.
  if (planMessage && planMessageHasUnfinishedBuild(planMessage)) {
    return 'unfinished';
  }

  return 'start';
}
